import { Button, Input, Card, Typography } from "antd";
import React, { useState, useCallback, useMemo } from "react";
import { supabase } from "../../../config/supabaseClient";
import "./EmailFlow.css";
import { sendWelcomeEmailToSubscriber } from "../../../Utils/SupabaseServices/EmailService";
import { useDesign } from "../../../context/DesignContext";
import {
  getShopParityButtonStyle,
  lightenColor,
} from "../../../Utils/StayUpToDate/RenderComponent";
import { isHexDark } from "../../../Utils/HelperFunction/HelperFunction";
import { darkenColor } from "../StayUpToDate/LandingPageComponents/LinksComponent/Components/hoverUtils";
import { E } from "../../DashboardView/MasterTemplateEditor/InlineEdit";
import { useTemplateAnalyticsOptional } from "../../DashboardView/MasterTemplateEditor/TemplateAnalyticsContext";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../Utils/SupabaseServices/templateAnalyticsEvents";
// import { isMobile } from "react-device-detect";

const CampaignForm = (props: any) => {
  const {
    data,
    user_id,
    background_color,
    designTokens,
    from,
    isMobileView = false,
  } = props;
  const designCtx = useDesign();
  const templateAnalytics = useTemplateAnalyticsOptional();
  let campaignName = data.campaign_name || data.name;
  let buttonText = data.campaign_button_text;
  let campaignId = data.id;
  let metadata: any = {};
  let bgColor = background_color;
  let imageUrl = data.image;
  let brand_color = props.brand_color;
  const {
    templateName,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    forthColor,
    fontFamily,
  } = designTokens || {};
  const isTemplateSite = from === "website-template";
  const stayRowId = data?.id != null ? String(data.id).trim() : "";
  const campaignNamePath = stayRowId ? `sutc.${stayRowId}.name` : "";
  const campaignCtaPath = stayRowId ? `sutc.${stayRowId}.cta_placeholder` : "";

  try {
    const details =
      typeof data.details === "string"
        ? JSON.parse(data.details)
        : data.details;
    if (details) {
      campaignName = details.campaign_name || campaignName;
      buttonText = details.campaign_button_text || buttonText;
      bgColor = details.background_color || bgColor;
      campaignId = details.campaign_id || campaignId;
      metadata = details;
      if (details.image) imageUrl = details.image;
    }
  } catch {}

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fieldValues, setFieldValues] = useState<{ [key: string]: string }>({});
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= 768 || isMobileView,
  );
  // Update isMobile on window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768 || isMobileView);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Memoize email regex for better performance
  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

  // Error message styling
  const errorMessageStyle = {
    color: "#ff4d4f",
    fontSize: 12,
    // marginTop: 4,
    lineHeight: 1.5,
    display: "flex" as const,
    alignItems: "center" as const,
    gap: 4,
  };

  // Memoize field validation function
  const validateField = useCallback(
    (field: any, value: string) => {
      if (field.required && !value) {
        return `Please enter ${field.label}`;
      }
      if (
        field.label.trim().toLowerCase() === "email" &&
        value &&
        !emailRegex.test(value)
      ) {
        return "Please enter a valid email address";
      }
      return "";
    },
    [emailRegex],
  );

  const handleSubmit = async (e: React.FormEvent, campaignId: string) => {
    e.preventDefault();

    // Quick validation using memoized function
    let hasError = false;
    const newFieldErrors: { [key: string]: string } = {};

    if (Array.isArray(metadata.fields)) {
      for (const field of metadata.fields) {
        const value = fieldValues[field.label];
        const error = validateField(field, value);
        if (error) {
          newFieldErrors[field.label] = error;
          hasError = true;
        }
      }
    }
    setFieldErrors(newFieldErrors);

    if (hasError) return;

    setLoading(true);

    try {
      // Prepare data efficiently
      const normalizedEmail = email.trim().toLowerCase();
      const dynamicFieldData = Array.isArray(metadata.fields)
        ? metadata.fields.map((field: any) => ({
            [field.label]: fieldValues[field.label] || "",
          }))
        : [];

      // Single database operation - insert subscriber
      const { data: newSubscriber, error } = await supabase
        .from("campaign_subscriber")
        .insert([
          {
            user_id,
            details: normalizedEmail,
            campaign_id: campaignId,
            campaign_name: campaignName,
            other_details:
              dynamicFieldData.length > 0
                ? JSON.stringify(dynamicFieldData)
                : null,
            email_sent: false, // Set to false initially
            email_type: "welcome", // Set email_type to 'welcome' for new subscribers
          },
        ])
        .select();

      if (error) {
        console.error("Failed to add subscriber:", error);
        setLoading(false);
        return;
      }

      // Show success immediately for better UX
      setLoading(false);
      setSuccess(true);

      templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.NEWSLETTER_SUBMIT, {
        sectionId: "newsletter",
        sectionType: "Newsletter",
        entityType: "form",
        entityId: String(campaignId),
        entityLabel: campaignName,
        details: { campaign_id: campaignId, campaign_name: campaignName },
      });

      // Handle email sending asynchronously (non-blocking)
      if (newSubscriber && newSubscriber[0]) {
        // Send email in background without blocking UI
        handleEmailSending(newSubscriber[0], campaignId, normalizedEmail);
      }

      // Reset form after delay
      setTimeout(() => {
        setSuccess(false);
        setEmail("");
        setFieldValues({});
        setFieldErrors({});
      }, 5000);
    } catch (error) {
      console.error("Subscription error:", error);
      setLoading(false);
    }
  };

  // Separate function for email sending (non-blocking)
  const handleEmailSending = async (
    subscriber: any,
    campaignId: string,
    normalizedEmail: string,
  ) => {
    try {
      // Get campaign data and user data in parallel
      const [campaignResult, userResult] = await Promise.allSettled([
        supabase
          .from("creators_campaigns")
          .select("*")
          .eq("id", campaignId)
          .single(),
        supabase
          .from("user")
          .select("name, email")
          .eq("user_id", user_id)
          .single(),
      ]);

      const campaign =
        campaignResult.status === "fulfilled"
          ? campaignResult.value.data
          : null;
      const userData =
        userResult.status === "fulfilled" ? userResult.value.data : null;

      // Prefer welcome-specific template, fallback to legacy fields
      const welcomeSubject =
        campaign?.welcome_email_subject || campaign?.email_subject || "";
      const welcomeBody =
        campaign?.welcome_email_body || campaign?.email_body || "";

      if (campaign && welcomeBody) {
        const subscriberData = {
          ...subscriber,
          details: normalizedEmail,
        };

        // Create a campaign object carrying the chosen welcome template
        const campaignWithWelcomeTemplate = {
          ...campaign,
          email_subject: welcomeSubject,
          email_body: welcomeBody,
        };

        const emailResult = await sendWelcomeEmailToSubscriber(
          subscriberData,
          campaignWithWelcomeTemplate,
          userData?.name,
          userData?.email,
        );

        // Update email_sent status and email_history based on result
        const updateData: any = {
          email_sent: emailResult.success,
          email_type: "welcome",
        };

        // If email was sent successfully, save email history
        if (emailResult.success) {
          const emailHistoryData = {
            subject: welcomeSubject,
            body: welcomeBody,
            sent_at: new Date().toISOString(),
          };
          updateData.email_history = JSON.stringify(emailHistoryData);
        }

        await supabase
          .from("campaign_subscriber")
          .update(updateData)
          .eq("id", subscriber.id);

        if (!emailResult.success) {
          console.error("Failed to send welcome email:", emailResult.message);
        }
      }
    } catch (error) {
      console.error("Error in background email sending:", error);
    }
  };

  const mergedTokensForShop = useMemo(
    () => ({
      templateName: designTokens?.templateName ?? designCtx.templateName,
      secondaryColor: designTokens?.secondaryColor ?? designCtx.secondaryColor,
      primaryColor: designTokens?.primaryColor ?? designCtx.primaryColor,
      tertiaryColor: designTokens?.tertiaryColor ?? designCtx.tertiaryColor,
      primaryFontFamily: designTokens?.primaryFontFamily ?? designCtx?.primaryFont,
      secondaryFontFamily: designTokens?.secondaryFontFamily ?? designCtx?.secondaryFont,
      forthColor: designTokens?.forthColor ?? designCtx?.forthColor
    }),
    [
      designCtx.primaryColor,
      designCtx.secondaryColor,
      designCtx.templateName,
      designCtx.tertiaryColor,
      designTokens?.primaryColor,
      designTokens?.secondaryColor,
      designTokens?.templateName,
      designTokens?.tertiaryColor,
      designTokens?.primaryFontFamily,
      designTokens?.secondaryFontFamily,
      designTokens?.forthColor,
      designCtx?.forthColor,
    ],
  );

  // Determine button background based on theme (fallback to DesignContext.templateName)
  const effectiveTemplateName = (
    (mergedTokensForShop.templateName ? mergedTokensForShop.templateName :
    templateName) ||
    ""
  ).toLowerCase();
  const isDefaultTheme = (effectiveTemplateName as string).toLowerCase() === "default";
  const buttonBg = isDefaultTheme
    ? "#000"
    : designTokens?.forthColor || "#000";
  const buttonOverrideClass = isDefaultTheme ? "campaign-btn-override" : "";
  const hoverBg = buttonBg ? isHexDark(buttonBg) ? lightenColor(buttonBg, 20) : darkenColor(buttonBg, 10) : "";
  // Shop-parity CTA: same fill rules as StoreProductCard (black on default,
  // forthColor otherwise). Do not pass secondaryColor as the fill — that made
  // default CTAs ink-on-ink and hover-flash via getShopParity's #d1d2d5 path.
  const applyFollowerShopParity = true;
  const shopParityForCampaign = getShopParityButtonStyle({
    designTokens: isDefaultTheme
      ? { ...mergedTokensForShop, templateName: "campaign" }
      : mergedTokensForShop,
    brand_color,
    contextButtonBackground: isDefaultTheme
      ? "#000000"
      : mergedTokensForShop.forthColor || brand_color || "#000",
  });
  const campaignCtaFg = isDefaultTheme
    ? "#ffffff"
    : shopParityForCampaign.color;
  const campaignHoverBg = isDefaultTheme
    ? lightenColor("#000000", 20)
    : shopParityForCampaign.hoverBg;

  const campaignNameEl = (
    <p className="campaign-name" style={{ fontFamily: mergedTokensForShop?.primaryFontFamily }}>
      {isTemplateSite && campaignNamePath ? (
        <E path={campaignNamePath}>{campaignName}</E>
      ) : (
        campaignName
      )}
    </p>
  );

  const subscribeLabel = success ? "Subscribed!" : buttonText;
  const subscribeButtonInner =
    isTemplateSite && campaignCtaPath && !success ? (
      <E path={campaignCtaPath}>{subscribeLabel}</E>
    ) : (
      subscribeLabel
    );

  return (
    <div
      className="campaign-card-container"
      style={{
        marginTop: "20px",
        width: "100%",
        border: designTokens?.secondaryColor ? `none` : "",
        fontFamily: fontFamily || undefined,
      }}
    >
      <style>{`
        .campaign-success-btn,
        .campaign-success-btn:hover,
        .campaign-success-btn:focus,
        .campaign-success-btn:active {
          background: #c8e6c9 !important;
          border-color: #c8e6c9 !important;
          color: #2e7d32 !important;
        }

        .campaign-success-btn span,
        .campaign-success-btn:hover span,
        .campaign-success-btn:focus span,
        .campaign-success-btn:active span {
          color: #2e7d32 !important;
        }
        ${
          applyFollowerShopParity
            ? `
        .campaign-subscribe-btn.follower-shop-parity-btn:hover:not(:disabled) {
          background: var(--hover-bg) !important;
          background-color: var(--hover-bg) !important;
          border-color: var(--hover-bg) !important;
          opacity: 1 !important;
        }
        .campaign-subscribe-btn.follower-shop-parity-btn:hover:not(:disabled) span {
          color: var(--follower-cta-fg) !important;
        }
        `
            : `
        .campaign-subscribe-btn:hover:not(:disabled) {
          background: ${hoverBg || buttonBg} !important;
          border-color: ${hoverBg || buttonBg} !important;
        }
        .campaign-subscribe-btn:hover:not(:disabled) span {
          color: ${isDefaultTheme ? "#fff" : designTokens?.tertiaryColor || "#fff"} !important;
        }
        ${
          isDefaultTheme
            ? `
          .uptodate-wrapper .campaign-btn-override,
          .uptodate-wrapper .campaign-btn-override:hover,
          .uptodate-wrapper .campaign-btn-override:focus,
          .uptodate-wrapper .campaign-btn-override:active {
            color: #fff !important;
          }
          .uptodate-wrapper .campaign-btn-override span { color: #fff !important; }
          .uptodate-wrapper .campaign-btn-override:hover {
            background: ${hoverBg || buttonBg} !important;
            border-color: ${hoverBg || buttonBg} !important;
            opacity: 1 !important;
          }
        `
            : ""
        }
        `
        }
      `}</style>
      <Card
        className="campaign-card"
        style={{
          borderRadius: 16,
          padding: "0 !important",
          backgroundColor: designTokens?.primaryColor || brand_color,
          boxShadow: "none",
          fontFamily: fontFamily || undefined,
        }}
       styles={
          isMobileView
            ? {
                body: {
                  padding: 10,
                },
              }
            : undefined
        }
      >
        {imageUrl ? (
          <>
            {/* Mobile: Campaign name above image when there's an image */}
            {isMobile && (
              <>
                {campaignNameEl}
              </>
            )}
            <div
              className={`campaign-image-container ${isMobile ? "campaign-mobile" : ""}`}
            >
              {/* Left: Image */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={imageUrl}
                  alt="Campaign"
                  className={`campaign-image ${isMobile ? "campaign-mobile-image" : ""}`}
                />
              </div>
              {/* Right: Form */}
              <div className="campaign-form-container-right">
                {!isMobile && (
                  <div className="mobile-campaign-name">
                    {campaignNameEl}
                  </div>
                )}
                <form
                  onSubmit={(e) => handleSubmit(e, campaignId)}
                  noValidate
                  style={{ width: "100%" }}
                >
                  {/* Render dynamic fields from metadata.fields */}
                  {Array.isArray(metadata.fields) &&
                    metadata.fields.length > 0 &&
                    metadata.fields.map((field: any, idx: number) => {
                      const isEmailField =
                        field.label.trim().toLowerCase() === "email";
                      return (
                        <div
                          key={idx}
                          style={{
                            marginBottom: 8,
                            fontFamily: mergedTokensForShop?.secondaryFontFamily
                          }}
                        >
                          <label style={{ display: "block", marginBottom: 4 }}>
                            {field.label}
                            {field.required && (
                              <span style={{ color: "red", marginLeft: 4 }}>
                                *
                              </span>
                            )}
                          </label>
                          <Input
                            type={"text"}
                            // isEmailField ? "email" : "text"
                            placeholder={field.label}
                            value={fieldValues[field.label] || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFieldValues((prev) => ({
                                ...prev,
                                [field.label]: value,
                              }));

                              // Clear error if field has value and is valid
                              if (fieldErrors[field.label]) {
                                const error = validateField(field, value);
                                if (!error) {
                                  setFieldErrors((prev) => ({
                                    ...prev,
                                    [field.label]: "",
                                  }));
                                }
                              }
                            }}
                            className="campaign-form-input"
                            style={{
                              backgroundColor: success ? "#f5f5f5" : "#fff",
                              cursor: success ? "not-allowed" : "text",
                              color: "#111",
                            }}
                            disabled={success}
                          />
                          <div
                            className={`campaign-form-error-slot${
                              fieldErrors[field.label]
                                ? " campaign-form-error-msg"
                                : ""
                            }`}
                            style={
                              fieldErrors[field.label]
                                ? errorMessageStyle
                                : undefined
                            }
                            role={
                              fieldErrors[field.label] ? "alert" : undefined
                            }
                            aria-hidden={
                              fieldErrors[field.label] ? undefined : true
                            }
                          >
                            {fieldErrors[field.label] ? (
                              <>
                                <span>•</span>
                                <span>{fieldErrors[field.label]}</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  <Button
                    // type="primary"
                    block
                    htmlType="submit"
                    loading={loading}
                    disabled={success}
                    className={
                      success
                        ? "campaign-subscribe-btn campaign-success-btn"
                        : !applyFollowerShopParity
                          ? `${buttonOverrideClass} campaign-subscribe-btn`.trim()
                          : "campaign-subscribe-btn follower-shop-parity-btn custom-button-with-hover"
                    }
                    style={
                      success
                        ? {
                            color: "#2e7d32",
                            background: "#c8e6c9",
                            fontSize: 14,
                            borderRadius: 8,
                            fontWeight: 500,
                            transition: "background 0.3s, border-color 0.3s",
                            marginTop: 20,
                            fontFamily: mergedTokensForShop?.secondaryFontFamily
                          }
                        : applyFollowerShopParity && shopParityForCampaign
                          ? {
                              ...shopParityForCampaign.structural,
                              backgroundColor:
                                shopParityForCampaign.backgroundColor,
                              borderColor:
                                shopParityForCampaign.backgroundColor,
                              color: campaignCtaFg,
                              ["--follower-cta-bg" as string]:
                                shopParityForCampaign.backgroundColor,
                              ["--follower-cta-fg" as string]: campaignCtaFg,
                              ["--hover-bg" as string]: campaignHoverBg,
                              border: "none",
                              boxShadow: "none",
                              borderRadius: 8,
                              transition:
                                "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
                              marginTop: 20,
                              fontFamily: mergedTokensForShop?.secondaryFontFamily
                            }
                          : {
                              ...(isDefaultTheme && {color: isDefaultTheme
                                ? designTokens?.primaryColor || "#fff"
                                : `${
                                    designTokens?.secondaryColor
                                      ? designTokens?.secondaryColor
                                      : "#fff"
                                  }`,
                              background: buttonBg}),
                              fontSize: 14,
                              borderRadius: 8,
                              fontWeight: 500,
                              transition: "background 0.3s, border-color 0.3s",
                              marginTop: 20,
                              fontFamily: mergedTokensForShop?.secondaryFontFamily
                            }
                    }
                  >
                    {subscribeButtonInner}
                  </Button>
                </form>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Mobile: Campaign name above form when no image */}
            <div>
              {campaignNameEl}
            </div>
            <form onSubmit={(e) => handleSubmit(e, campaignId)} noValidate>
              {/* Render dynamic fields from metadata.fields */}
              {Array.isArray(metadata.fields) &&
                metadata.fields.length > 0 &&
                metadata.fields.map((field: any, idx: number) => {
                  const isEmailField =
                    field.label.trim().toLowerCase() === "email";
                  return (
                    <div key={idx} style={{ marginBottom: 8, fontFamily: mergedTokensForShop?.secondaryFontFamily }}>
                      <label style={{ display: "block", marginBottom: 4 }}>
                        {field.label}
                        {field.required && (
                          <span style={{ color: "red", marginLeft: 4 }}>*</span>
                        )}
                      </label>
                      <Input
                        type="text"
                        placeholder={field.label}
                        value={fieldValues[field.label] || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFieldValues((prev) => ({
                            ...prev,
                            [field.label]: value,
                          }));

                          // Clear error if field has value and is valid
                          if (fieldErrors[field.label]) {
                            const error = validateField(field, value);
                            if (!error) {
                              setFieldErrors((prev) => ({
                                ...prev,
                                [field.label]: "",
                              }));
                            }
                          }
                        }}
                        className="campaign-form-input"
                        style={{
                          marginBottom: 8,
                          height: 35,
                          fontSize: 14,
                          borderRadius: 8,
                          backgroundColor: "#fff",
                          color: "#111",
                        }}
                        disabled={success}
                      />
                      <div
                        className={`campaign-form-error-slot${
                          fieldErrors[field.label]
                            ? " campaign-form-error-msg"
                            : ""
                        }`}
                        style={
                          fieldErrors[field.label]
                            ? errorMessageStyle
                            : undefined
                        }
                        role={fieldErrors[field.label] ? "alert" : undefined}
                        aria-hidden={
                          fieldErrors[field.label] ? undefined : true
                        }
                      >
                        {fieldErrors[field.label] ? (
                          <>
                            <span>•</span>
                            <span>{fieldErrors[field.label]}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              <Button
                // type="primary"
                block
                htmlType="submit"
                loading={loading}
                disabled={success}
                className={
                  success
                    ? "campaign-subscribe-btn campaign-success-btn"
                    : !applyFollowerShopParity
                      ? `${buttonOverrideClass} campaign-subscribe-btn`.trim()
                      : "campaign-subscribe-btn follower-shop-parity-btn custom-button-with-hover"
                }
                style={
                  success
                    ? {
                        color: "#2e7d32",
                        background: "#c8e6c9",
                        fontSize: 14,
                        borderRadius: 8,
                        fontWeight: 500,
                        border: "none",
                        transition: "background 0.3s, border-color 0.3s",
                        marginTop: 20,
                        fontFamily: mergedTokensForShop?.secondaryFontFamily
                      }
                    : applyFollowerShopParity && shopParityForCampaign
                      ? {
                          ...shopParityForCampaign.structural,
                          backgroundColor:
                            shopParityForCampaign.backgroundColor,
                          borderColor: shopParityForCampaign.backgroundColor,
                          color: campaignCtaFg,
                          ["--follower-cta-bg" as string]:
                            shopParityForCampaign.backgroundColor,
                          ["--follower-cta-fg" as string]: campaignCtaFg,
                          ["--hover-bg" as string]: campaignHoverBg,
                          border: "none",
                          boxShadow: "none",
                          borderRadius: 8,
                          transition:
                            "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
                          marginTop: 20,
                          fontFamily: mergedTokensForShop?.secondaryFontFamily
                        }
                      : {
                          ...(isDefaultTheme && {color: isDefaultTheme
                                ? designTokens?.primaryColor || "#fff"
                                : `${
                                    designTokens?.secondaryColor
                                      ? designTokens?.secondaryColor
                                      : "#fff"
                                  }`,
                              background: buttonBg}),
                          fontSize: 14,
                          borderRadius: 8,
                          fontWeight: 500,
                          border: "none",
                          transition: "background 0.3s, border-color 0.3s",
                          marginTop: 20,
                          fontFamily: mergedTokensForShop?.secondaryFontFamily
                        }
                }
              >
                {subscribeButtonInner}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
};

export default CampaignForm;
